import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./Navbar.css";
import logo from "../assets/logo.png";
import LoginModal from "./LoginModal";
import SignUpModal from "./SignUpModal";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createAccountApi, loginApi, logoutApi, getUserProfileApi } from "../services/authService";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { auth } from "../services/firebase/auth";
import { ErrorHandler, SuccessToast } from "utils/toast";
import ForgotPassword from "./ForgotPassword";
import ConfirmationModal from "./ConfirmationModal";
import LogoutSVG from "assets/svg/LogoutSVG";
import EmailVerificationModal from "./EmailVerificationModal";
import ScrollTopLink from "./ScrollTopLink";
import { useRedirectTo } from "hooks/useRedirectTo";

function Navbar() {
  const { isAuthenticated, logout, login, user: authData, updateUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { createRedirectUrl } = useRedirectTo();

  // Call user detail API in Navbar to check whether user is eligible for subscription
  const { data: profileData } = useQuery({
    queryKey: ["userProfile"],
    queryFn: getUserProfileApi,
    enabled: Boolean(isAuthenticated),
    staleTime: 1000 * 60 * 2,
  });

  useEffect(() => {
    if (profileData?.payload && updateUser) {
      updateUser(profileData.payload);
    }
  }, [profileData?.payload, updateUser]);

  const isEligibleForSubscription = Boolean(
    profileData?.payload?.isEligibleForSubscription ??
    (authData?.isEligibleForSubscription === true ||
      authData?.isEligibleForSubscription === "true")
  );
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showEmailVerifiedModal, setShowEmailVerifiedModal] = useState(false);
  const [user, setUser] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const openLoginHandler = () => setShowLoginModal(true);
    window.addEventListener("openLoginModal", openLoginHandler);

    return () => {
      window.removeEventListener("openLoginModal", openLoginHandler);
    };
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  const handleCloseSignUpModal = () => {
    setShowSignUpModal(false);
  };

  const handleCloseForgotPasswordModal = () => {
    setShowForgotPasswordModal(false);
  };

  //Sign In Api Call
  const { mutateAsync: mutateLoginAsync } = useMutation({
    mutationFn: loginApi,
    onSuccess: (data) => {
      console.log("Access Token", data?.payload?.accessToken);
      login(data?.payload);
      setShowLoginModal(false); // Close modal on success
      navigate("/");
      SuccessToast("Logged in successfully!");
    },
    onError: (error) => {
      console.error("Error on Signin:", error);
      ErrorHandler(error);
    },
  });

  //Resend Verification Email
  const handleResend = async () => {
    try {
      setIsLoading(true);
      await sendEmailVerification(user);
      SuccessToast("Verification email resent!");
    } catch (err) {
      ErrorHandler(err);
      console.error(err);
    } finally {
      setShowEmailVerifiedModal(false);
      setIsLoading(false);
    }
  };

  const handleSignIn = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential?.user;

      await user?.reload();

      if (!user.emailVerified) {
        setUser(user);
        setShowEmailVerifiedModal(true);
        setShowLoginModal(false);
        return;
      }

      const token = await user?.getIdToken();
      console.log("Firebase user signed in:", token);
      await mutateLoginAsync(token);
    } catch (firebaseError) {
      console.error("Login failed:", firebaseError);
      throw firebaseError;
    }
  };

  //Create Account Api Call
  const { mutateAsync: mutateCreateAccountAsync } = useMutation({
    mutationFn: createAccountApi,
    onSuccess: async (data) => {
      console.log("Account created successfully:", data);
      await sendEmailVerification(user);
      setShowSignUpModal(false); // Close modal on success
      SuccessToast("Please verify your email address before signing in.");
    },
    onError: (error) => {
      console.error("Error creating account:", error);
      ErrorHandler(error);
    },
  });

  const handleSignUp = async (fullName, gender, email, password) => {
    console.log("User signed up:", { fullName, gender, email, password });
    // Add your sign up logic here
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const token = await userCredential?.user?.getIdToken();
      setUser(userCredential?.user);
      console.log("Firebase user created with token:", token);

      await mutateCreateAccountAsync({
        token,
        fullName,
        gender,
        email,
        password,
      });
    } catch (firebaseError) {
      console.error("Sign up failed:", firebaseError);
      throw firebaseError;
    }
  };

  const handleSwitchToSignUp = () => {
    setShowLoginModal(false);
    setShowSignUpModal(true);
  };

  const handleSwitchToSignIn = () => {
    if (showSignUpModal) {
      setShowSignUpModal(false);
    } else {
      setShowForgotPasswordModal(false);
    }
    setShowLoginModal(true);
  };

  const handleSwitchToForgotPassword = () => {
    console.log("Redirecting to forgot password...");
    // Add your forgot password logic here
    setShowLoginModal(false);
    setShowForgotPasswordModal(true);
  };

  const handleForgotPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      setShowForgotPasswordModal(false); // Close modal on success
      SuccessToast("Password reset link has been sent to your email.");
    } catch (firebaseError) {
      ErrorHandler(firebaseError);
      throw firebaseError; // Re-throw so modal knows it failed
    }
  };

  //Logout Api Call
  const { mutate: mutateLogout } = useMutation({
    mutationFn: logoutApi,
    onSuccess: () => {
      logout();
      queryClient.clear();
      navigate("/", {
        state: { logout: true },
      });
    },
    onError: (error) => {
      console.error("Error during logout:", error);
      logout();
      queryClient.clear();
      ErrorHandler(error);
      navigate("/", {
        state: { logout: true },
      });
    },
    onSettled: () => {
      setIsLoading(false);
      setShowLogoutModal(false);
    },
  });

  const handleLogout = async () => {
    setIsLoading(true);
    mutateLogout();
  };

  const handleLogoutModalOpen = () => {
    setShowLogoutModal(true);
  };
  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-logo">
            <div className="logo-img">
              <img
                src={logo}
                alt="Logo"
                style={{ width: 32, height: 32, borderRadius: "50%" }}
              />
            </div>
          </div>

          {/* Hamburger Button */}
          <button
            className={`hamburger ${isMobileMenuOpen ? "active" : ""}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* Desktop & Mobile Navigation */}
          <div className={`navbar-links ${isMobileMenuOpen ? "active" : ""}`}>
            <ScrollTopLink
              to="/"
              className="navbar-link"
              onClick={closeMobileMenu}
            >
              Home
            </ScrollTopLink>
            <ScrollTopLink
              to="/about"
              className="navbar-link"
              onClick={closeMobileMenu}
            >
              About
            </ScrollTopLink>
            {/* <ScrollTopLink
              to="/#how-it-works"
              className="navbar-link"
              onClick={closeMobileMenu}
            >
              How It Works
            </ScrollTopLink> */}
            <ScrollTopLink
              to="/contact"
              className="navbar-link"
              onClick={closeMobileMenu}
            >
              Contact
            </ScrollTopLink>
            <Link
              to={createRedirectUrl("")}
              className="navbar-link"
              onClick={closeMobileMenu}
            >
              Dashboard
            </Link>

            {isAuthenticated && isEligibleForSubscription && (
              <ScrollTopLink
                to="/subscription"
                className="navbar-subscription-btn"
                onClick={closeMobileMenu}
              >
                Subscription
              </ScrollTopLink>
            )}

            {isAuthenticated ? (
              <div className="user-menu">
                {/* <span className="user-name">Hi, {user?.name}</span> */}
                <button
                  className="questionnaire-btn"
                  onClick={() => {
                    closeMobileMenu();
                    navigate("/questionnaire");
                  }}
                >
                  Questionnaire
                </button>
                <button
                  className="logout-btn"
                  onClick={() => {
                    closeMobileMenu();
                    handleLogoutModalOpen();
                  }}
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                className="login-btn"
                onClick={() => {
                  closeMobileMenu();
                  handleLoginClick();
                }}
              >
                Login
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div className="navbar-overlay" onClick={closeMobileMenu}></div>
      )}

      <ConfirmationModal
        show={showLogoutModal}
        onCancel={handleLogoutCancel}
        onConfirm={handleLogout}
        title="Logout"
        description="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Close"
        icon={<LogoutSVG />}
        isLoading={isLoading}
      />

      <LoginModal
        show={showLoginModal}
        handleClose={handleCloseLoginModal}
        onSignIn={handleSignIn}
        onSignUp={handleSwitchToSignUp}
        onForgotPassword={handleSwitchToForgotPassword}
      />

      <SignUpModal
        show={showSignUpModal}
        handleClose={handleCloseSignUpModal}
        onSignUp={handleSignUp}
        onSignIn={handleSwitchToSignIn}
      />

      <ForgotPassword
        show={showForgotPasswordModal}
        handleClose={handleCloseForgotPasswordModal}
        onSignIn={handleSwitchToSignIn}
        onForgotPassword={handleForgotPassword}
      />

      <EmailVerificationModal
        onResendEmail={handleResend}
        isOpen={showEmailVerifiedModal}
        email={user?.email}
        onClose={() => setShowEmailVerifiedModal(false)}
        isLoading={isLoading}
      />
    </>
  );
}

export default Navbar;
